/**
 * TX-01 Colloquial texture (INV-CH-TX-01, ENGINE-SPEC §4). Kept from the seed:
 * `fills + initFill + initConj + 1.5·rq` per 1,000 prose words, two-way, descending ramp.
 *
 *   fills     discourse fillers ("honestly", "sort of", …) anywhere in a prose block
 *   initFill  sentences opening with Well/Look/Granted/Now/Anyway/Honestly/Frankly
 *   initConj  sentences opening with And/But/So/Or/Because
 *   rq        a question answered by a short statement ("Why? Because.")
 *
 * Sentences come from the document model (prose register, one-word sentences dropped as the seed's
 * tokenizer did); fillers and rq run once per prose block.
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseParagraphs, proseSentenceList } from './helpers.ts';
import type { ChannelDef, Measurement, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0, 58],
	[3, 46],
	[8, 32],
	[15, 20],
	[30, 8],
];

const F_FILL = 1;
const F_INIT_FILL = 2;
const F_INIT_CONJ = 3;
const F_RQ = 4;
const W_RQ = 1.5;

export const tx01Def: ChannelDef = {
	id: 'TX-01',
	name: 'Colloquial texture',
	family: 'TEXTURE',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'rate',
	features: [
		{ id: 'texture', label: 'Texture units per 1,000 words' },
		{ id: 'filler', label: 'Discourse fillers' },
		{ id: 'initialFiller', label: 'Sentence-initial fillers (Well, Look, Now …)' },
		{ id: 'initialConjunction', label: 'Sentence-initial And/But/So/Or/Because' },
		{ id: 'questionShortAnswer', label: 'Question then short answer (×1.5)' },
	],
	minExposure: 350,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1000,
	unitLabel: 'per 1k words',
	prior: derivePrior(RAMP, 1000, 'words', 1.5),
	weight: 0.8,
	nd: 1,
	// initial fillers/conjunctions use the document-model sentences (C-SPL: scene breaks and paragraph
	// starts are boundaries) and prose-register paragraphs (C-REG: the seed's line heuristic peeled
	// short and colon-led lines out of its sentence list)
	changes: ['C-SPL', 'C-REG'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

const TWO_WORDS = /\S\s+\S/;

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = lex().channels['ch-tx01'];
	const reInitFill = nr.get('initialFiller');
	const reInitConj = nr.get('initialConjunction');
	const iRq = nr.index.questionShortAnswer as number;
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	let fills = 0;
	let initFill = 0;
	let initConj = 0;
	let rq = 0;

	for (const p of proseParagraphs(doc)) {
		e[p] = doc.paragraphs.ownWords[p] as number;
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		let kp = 0;
		// the 20-literal prefilter costs more than the (anchored-by-literals) regex itself: scan directly
		{
			const re = nr.get('fillers');
			re.lastIndex = 0;
			for (let m = re.exec(text); m; m = re.exec(text)) {
				ev.add(b.charStart + m.index, b.charStart + m.index + m[0].length, F_FILL, 1);
				kp += 1;
				fills++;
			}
			re.lastIndex = 0;
		}
		if (nr.list.admits(iRq, text, text)) {
			const re = nr.get('questionShortAnswer');
			re.lastIndex = 0;
			for (let m = re.exec(text); m; m = re.exec(text)) {
				ev.add(b.charStart + m.index, b.charStart + m.index + m[0].length, F_RQ, W_RQ);
				kp += W_RQ;
				rq++;
			}
			re.lastIndex = 0;
		}
		k[p] = kp;
	}

	const pl = proseSentenceList(doc);
	const S = doc.sentences;
	for (let i = 0; i < pl.count; i++) {
		const s = pl.sent[i] as number;
		const a = S.charStart[s] as number;
		const text = doc.text.slice(a, S.charEnd[s] as number);
		const c0 = text.charCodeAt(0);
		// cheap reject: the openers all start with an ASCII capital
		if (c0 < 65 || c0 > 90 || !TWO_WORDS.test(text)) continue;
		const p = pl.para[i] as number;
		const mf = reInitFill.exec(text);
		if (mf) {
			ev.add(a, a + mf[0].length - 1, F_INIT_FILL, 1);
			k[p] = (k[p] as number) + 1;
			initFill++;
		}
		const mc = reInitConj.exec(text);
		if (mc) {
			ev.add(a, a + mc[0].length, F_INIT_CONJ, 1);
			k[p] = (k[p] as number) + 1;
			initConj++;
		}
	}

	const units = fills + initFill + initConj + W_RQ * rq;
	const words = doc.proseWords;
	const density = words > 0 ? (units / words) * 1000 : 0;
	const summary: string[] = [];
	if (fills + initFill > 0)
		summary.push(`${fills + initFill} discourse fillers: honestly / sort of / anyway …`);
	if (initConj > 0)
		summary.push(`${initConj} And/But/So openings: conversational conjunction starts`);
	if (rq > 0) summary.push(`${rq} question-then-short-answer: self-dialogue pattern`);
	summary.push(`${fmt(units, 1)} texture units · ${fmt(density, 1)}/1000w`);
	return {
		k,
		e,
		evidence: ev,
		features: { fills, initFill, initConj, questionShortAnswer: rq },
		summary,
	};
}

export const tx01 = rateChannel(tx01Def, measure);
export { tx01Def as def, tx01 as impl };
